<template>
	<div>
		<div class="flex">
			<div class="flex flex_column w_100">
				<AgItem class="flex_1"
						:columnDefs="columnDefs"
						:rowData="rowData"
						@sortChanged="sortChanged"
				></AgItem>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
		</div>
	</div>
</template>

<script>

import AgItem from "@/components/templates/agGrid/AgItem.vue";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";
import {enumDef} from "@/common/enumDefs";
import {asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import {prospectiveReceivableApi} from "@/common/api/prospectiveReceivableApi";
import {navigationUtils} from "@/common/navigation/navigationUtils";
import {navigationPaths} from "@/common/navigation/navigationPaths";
import {navigationTabs} from "@/common/navigation/navigationTabs";
import {formatAsLocalizedDate, formatAsMoneyAmount} from "@/common/format";

export default {
	extends: PaginationParent,
	components: {
		AgItem,
		Pagination
	},
	data() {
		return {
			rowData: null,
			columnDefs: [],
			sorting: []
		};
	},
	beforeMount() {
		this.setColumnDefs();
	},
	methods: {
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		setColumnDefs() {
			this.columnDefs = [
				{
					field: "receivableId",
					sort: "desc",
					hide: true
				},
				{
					headerName: this.$t("seller.marketSounding.table.header.marketSoundingStatus"),
					minWidth: 180,
					maxWidth: 180,
					field: "opportunityStatus",
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.upcomingOpportunityStatus,
					sortable: true
				},
				{
					headerName: this.$t("seller.marketSounding.table.header.prospectiveStatus"),
					field: "receivableState",
					minWidth: 160,
					maxWidth: 190,
					cellRendererFramework: "EnumAsTextWithLinkAction",
					cellRendererParams: {
						enumDef: enumDef.allReceivablesState,
						customization: {
							[enumDef.allReceivablesState.Selling]: {
								linkAction: (_) => this.$router.push(navigationUtils.getRouteWithActiveTab(navigationPaths.seller.sell, navigationTabs.seller.sell.selling))
							}
						}
					},
					sortable: false
				},
				{
					headerName: this.$t("common.type"),
					field: "receivableType",
					minWidth: 65,
					maxWidth: 80,
					cellRendererFramework: "EnumAsIcon",
					enumDef: enumDef.receivableType,
					sortable: true,
				},
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 200,
					sortable: true,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem"
				},
				{
					headerName: this.$t("common.invoiceNumber"),
					headerClass: "align-right",
					field: "prospectiveInvoiceNumber",
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueField: "convertedInvoiceNumber",
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data),
						compareFormatted: false
					},
					minWidth: 175,
					sortable: true,
				},
				{
					headerName: this.$t("common.invoiceDocument"),
					field: "invoiceDocumentFilename",
					minWidth: 175,
					cellRendererFramework: "FileLink",
					cellRendererParams: {
						fileIdField: "invoiceDocument",
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.grossAmount"),
					headerClass: "align-right",
					field: "prospectiveReceivableAmount",
					valueFormatter: asMoneyAmount(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsMoneyAmount(data.convertedReceivableAmount),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					minWidth: 175,
					sortable: true,
				},
				{
					headerName: this.$t("common.ccy"),
					field: "currency",
					minWidth: 75,
					maxWidth: 100,
					sortable: true,
				},
				{
					headerName: this.$t("common.invoiceDate"),
					field: "prospectiveInvoiceDate",
					headerClass: "align-right",
					minWidth: 150,
					maxWidth: 200,
					valueFormatter: asLocalizedDate(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedInvoiceDate),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.dueDate"),
					headerClass: "align-right",
					field: "prospectiveDueDate",
					minWidth: 150,
					maxWidth: 200,
					valueFormatter: asLocalizedDate(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedDueDate),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					maxWidth: 100,
					sortable: false,
					cellRendererFramework: "GeneralActionButton",
					cellRendererParams: {
						iconClass: "fa-search-plus",
						action: this.openEditForm
					},
					pinned: "right",
				}
			];
		},
		getList() {
			let postData = {
				columns: [],
				filters: [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting,
			};
			prospectiveReceivableApi.inspection.listProspectiveHistory(postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data.map(item => {
						return {
							...item,
							invoiceNumber: item.prospectiveInvoiceNumber, // To make sure other depending components using simple "invoiceNumber" field still work
							expired: false
						};
					});
				})
				.catch(this.toast);
		},
		openEditForm(data) {
			this.$store.dispatch("setActionPdfList", [data]);

			let showPdf = !!data.invoiceDocument;

			if (showPdf) {
				this.$store.dispatch("setActionActivePdf", data);
			} else {
				this.$store.dispatch("setActionActivePdf", {});
			}

			this.$store.dispatch("setActionPdfDialog", {
				show: true,
				prospective: true,
				hideLeft: !showPdf,
				data: data
			});
		},
		getConvertedValueComparisonSubtextKey(data) {
			if (data.receivableState === enumDef.allReceivablesState.Selling) {
				return "marketSounding.valueComparison.subtext.state.selling";
			} else if (data.opportunityConverted) {
				return "marketSounding.valueComparison.subtext.state.converted";
			} else {
				return null;
			}
		}
	}
};
</script>
